<template>
  <el-dialog
    :visible.sync="visibleProxy"
    title="工作日志详情"
    width="900px"
    append-to-body
    :close-on-click-modal="false"
  >
    <div v-loading="loading" class="detail-container">
      <p v-if="loadFailed" class="load-error">详情加载失败，请关闭后重试。</p>
      <template v-else-if="detail">
        <div class="section-title">基础信息</div>
        <div class="custom-desc">
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">日志日期</div>
              <div class="desc-value">{{ formatDate(detail.logDate) }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">状态</div>
              <div class="desc-value">
                <el-tag :type="detail.status === 'confirmed' ? 'success' : 'warning'" size="small" effect="light">
                  {{ detail.statusName || '-' }}
                </el-tag>
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">工作类型</div>
              <div class="desc-value">{{ formatWorkType(detail.workType) }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">实际发生时间</div>
              <div class="desc-value">{{ formatDateTime(detail.actualTime) }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">所属大区</div>
              <div class="desc-value">{{ detail.zoneAreaName || '-' }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">具体二级区域</div>
              <div class="desc-value">{{ detail.secondaryAreaName || '-' }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">主要执行人</div>
              <div class="desc-value">{{ detail.ownerUserName || '-' }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">协作人员</div>
              <div class="desc-value">{{ workerNames }}</div>
            </div>
          </div>
        </div>

        <div class="section-title mt-4">工作详情</div>
        <div class="custom-desc">
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">工作内容</div>
              <div class="desc-value desc-value--multiline">
                {{ detail.workContent || '-' }}
              </div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">具体位置</div>
              <div class="desc-value desc-value--multiline">{{ detail.workLocation || '-' }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">备注说明</div>
              <div class="desc-value desc-value--multiline">{{ detail.remark || '-' }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">工作结果</div>
              <div class="desc-value desc-value--multiline">{{ detail.workResult || '-' }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">材料使用</div>
              <div class="desc-value">{{ materialReplaced ? '已更换' : '未更换' }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">处理标记</div>
              <div class="desc-value">{{ processTypeName }}</div>
            </div>
          </div>
          <div v-if="materialReplaced" class="desc-row">
            <div class="desc-item">
              <div class="desc-label">材料明细</div>
              <div class="desc-value desc-value--multiline">{{ detail.materialSummary || '-' }}</div>
            </div>
          </div>
          <div v-if="processType !== 'normal'" class="desc-row">
            <div class="desc-item">
              <div class="desc-label">故障单编号</div>
              <div class="desc-value">
                <code class="highlight-code">{{ detail.faultCode || '-' }}</code>
              </div>
            </div>
          </div>
        </div>

        <div class="section-title mt-4">来源信息</div>
        <div class="custom-desc">
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">来源类型</div>
              <div class="desc-value">{{ detail.sourceTypeName || '-' }}</div>
            </div>
            <div class="desc-item">
              <div class="desc-label">来源动作</div>
              <div class="desc-value">{{ detail.sourceActionName || '-' }}</div>
            </div>
          </div>
          <div class="desc-row">
            <div class="desc-item">
              <div class="desc-label">来源明细</div>
              <div class="desc-value">{{ detail.sourceDetailCount || 0 }} 条 / {{ detail.deviceCount || 0 }} 台设备</div>
            </div>
          </div>
        </div>

        <template v-if="imageFileIds.length">
          <div class="section-title mt-4">工作图片</div>
          <ImageThumbViewer :file-ids="imageFileIds" variant="compact" preview-width="640px" img-alt="工作图片" />
        </template>
      </template>
    </div>
    <div slot="footer">
      <el-button @click="visibleProxy = false">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import ImageThumbViewer from '@/features/components/common-image/ImageThumbViewer.vue';
import { parseImageFileIds } from '@/features/components/common-image/imageFileIdsUtil.js';
import { formatDate, formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import { workLogService } from './work-log.service.js';
import { formatWorkType, normalizeProcessType } from './workLogDict.js';

export default {
  name: 'WorkLogDetailDialog',
  components: { ImageThumbViewer },
  props: {
    visible: { type: Boolean, default: false },
    logId: { type: String, required: true }
  },
  data () {
    return {
      detail: null,
      loading: false,
      loadFailed: false
    };
  },
  computed: {
    visibleProxy: {
      get () {
        return this.visible;
      },
      set (value) {
        this.$emit('update:visible', value);
      }
    },
    materialReplaced () {
      const record = this.detail || {};
      return (record.materialReplacedFlag || (record.materialSummary ? 'Y' : 'N')) === 'Y';
    },
    processType () {
      return normalizeProcessType(this.detail || {});
    },
    processTypeName () {
      if (this.processType === 'normal') return '普通记录';
      return this.processType === 'repair_completed' ? '故障维修完成' : '新增故障';
    },
    workerNames () {
      const names = (this.detail || {}).workerNames;
      return Array.isArray(names) ? names.filter(Boolean).join('、') || '-' : names || '-';
    },
    imageFileIds () {
      return parseImageFileIds((this.detail || {}).imageFileIds);
    }
  },
  methods: {
    formatDate,
    formatDateTime,
    formatWorkType,
    async loadDetail () {
      this.loading = true;
      this.loadFailed = false;
      try {
        const res = await workLogService.getDetail(this.logId);
        this.detail = res.data || null;
        this.loadFailed = !this.detail;
      } catch (error) {
        this.loadFailed = true;
      } finally {
        this.loading = false;
      }
    }
  },
  created () {
    this.loadDetail();
  }
};
</script>

<style lang="scss" scoped>
.detail-container {
  min-height: 160px;
  padding: 0 4px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 14px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1.1;
}

.mt-4 {
  margin-top: 20px;
}

.custom-desc {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
  font-size: 14px;
  background: #fff;
}

.desc-row {
  display: flex;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
  }
}

.desc-item {
  display: flex;
  flex: 1;
  min-width: 0;
  border-right: 1px solid #ebeef5;

  &:last-child {
    border-right: none;
  }
}

.desc-label {
  width: 120px;
  background-color: #f8f9fb;
  color: #606266;
  padding: 10px 6px 10px 12px;
  box-sizing: border-box;
  font-weight: 500;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.desc-value {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  color: #303133;
  box-sizing: border-box;
  word-break: break-all;
  display: flex;
  align-items: center;
  line-height: 1.6;
}

.desc-value--multiline {
  white-space: pre-wrap;
  align-items: flex-start;
}

.highlight-code {
  color: #409eff;
  background: #ecf5ff;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
}

.load-error {
  color: #909399;
  text-align: center;
}
</style>
